import React, { useState, useRef, useCallback } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Activity, AlertCircle, Camera, ChevronLeft, ChevronRight, ClipboardList, Clock, Film, Loader2, Pencil, Plus, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { inspectionsApi } from '../../services/api';
import { TabLoader } from './TabLoader';

const QATab = ({
  editData, updateField, canEdit, inspection, liveProgressData,
  selectedCategoryId, setSelectedCategoryId, categoriesToDisplay,
  editingAnswer, setEditingAnswer, savingAnswer, updateAnswer,
  viewQAImage, isInspCompleted, onRefresh,
}) => {
  const [uploadingMedia, setUploadingMedia] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const activeQuestionRef = useRef(null);
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const checkScrollArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 2);
    setShowRightArrow(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  const scrollCategories = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 250, behavior: 'smooth' });
    setTimeout(checkScrollArrows, 350);
  };

  // Check arrows on mount and when categories change
  React.useEffect(() => {
    setTimeout(checkScrollArrows, 300);
  }, [liveProgressData?.categories, checkScrollArrows]);

  const getAICategoryRating = (categoryName, categoryKey) => {
    const aiRatings = inspection?.ai_insights?.category_ratings;
    if (!aiRatings) return null;
    if (aiRatings[categoryKey] !== undefined) {
      const val = aiRatings[categoryKey];
      return typeof val === 'object' ? val.rating : val;
    }
    const normalized = categoryName?.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    if (normalized && aiRatings[normalized] !== undefined) {
      const val = aiRatings[normalized];
      return typeof val === 'object' ? val.rating : val;
    }
    const aiConditions = inspection?.ai_insights?.condition_ratings || {};
    const name = categoryName?.toLowerCase() || '';
    const condMap = (c) => { const u = c?.toUpperCase(); return u === 'POOR' ? 2 : (u === 'AVERAGE' || u === 'FAIR') ? 5 : u === 'GOOD' ? 7 : u === 'EXCELLENT' ? 9 : null; };
    if (name.includes('engine') || name.includes('health')) return condMap(aiConditions.engine);
    if (name.includes('exterior')) return condMap(aiConditions.exterior);
    if (name.includes('interior')) return condMap(aiConditions.interior);
    if (name.includes('transmission')) return condMap(aiConditions.transmission);
    return null;
  };

  const handleMediaUpload = (questionId, isVideo) => {
    activeQuestionRef.current = { questionId, isVideo };
    if (fileInputRef.current) {
      fileInputRef.current.accept = isVideo ? 'video/*' : 'image/*';
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeQuestionRef.current || !inspection?.id) return;
    const { questionId, isVideo } = activeQuestionRef.current;
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) { toast.error(`File too large. Max ${isVideo ? '50MB' : '10MB'}`); return; }

    setUploadingMedia(questionId);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const editState = editingAnswer?.questionId === questionId ? editingAnswer : null;
      if (editState?.pendingAnswer) formData.append('answer', editState.pendingAnswer);
      if (editState?.pendingSub1) formData.append('sub_answer_1', editState.pendingSub1);
      if (editState?.pendingSub2) formData.append('sub_answer_2', editState.pendingSub2);

      await inspectionsApi.uploadQuestionMedia(inspection.id, questionId, formData, (ev) => {
        setUploadProgress(Math.round((ev.loaded * 100) / (ev.total || ev.loaded)));
      });
      toast.success('Media uploaded successfully');
      setEditingAnswer(null);
      if (onRefresh) onRefresh(inspection.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload media');
    } finally {
      setUploadingMedia(null);
      setUploadProgress(0);
      activeQuestionRef.current = null;
    }
  };

  const getExpectedMediaType = (q) => {
    const qt = (q.question_type || '').toLowerCase();
    if (qt === 'video') return 'video';
    if (qt === 'photo' || qt === 'image' || qt === 'camera') return 'image';
    if (q.media_type === 'video') return 'video';
    if (q.media_type === 'image') return 'image';
    if (q.media_url && /video|\.mp4|\.mov|\.webm|\/videos\//i.test(q.media_url)) return 'video';
    if (q.media_url) return 'image';
    const txt = (q.question_text || '').toLowerCase();
    if (txt.includes('video')) return 'video';
    if (txt.includes('photo') || txt.includes('picture') || txt.includes('image') || txt.includes('click')) return 'image';
    return null;
  };

  /* ─── Helper: Format answer for display (filters out media refs) ─── */
  const formatAnswerForDisplay = (answer) => {
    if (!answer) return null;
    const answerStr = typeof answer === 'object' 
      ? (answer.selection || JSON.stringify(answer)) 
      : String(answer);
    // Filter out media references that shouldn't be displayed as text
    if (answerStr.startsWith('media_ref:') || answerStr.startsWith('data:') || 
        answerStr.startsWith('http') || answerStr.startsWith('gs://') ||
        answerStr.startsWith('file://') || answerStr.startsWith('/data/')) {
      return null;
    }
    return answerStr;
  };

  /* ─── Helper: Render answer display (main + subs + media) ─── */
  const renderAnswerDisplay = (q, currentAnswer) => {
    const hasMedia = !!q.media_url;
    const isVideoQuestion = getExpectedMediaType(q) === 'video';
    const formattedAnswer = formatAnswerForDisplay(currentAnswer);
    const formattedSub1 = formatAnswerForDisplay(q.sub_answer_1);
    const formattedSub2 = formatAnswerForDisplay(q.sub_answer_2);
    const isValidTextAnswer = !!formattedAnswer;

    return (
      <div className="space-y-1.5">
        {/* Row: Media + text answer side by side */}
        <div className="flex items-start gap-2.5">
          {/* Media thumbnail */}
          {hasMedia && (
            isVideoQuestion ? (
              <div className="relative h-14 w-20 rounded border overflow-hidden cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0 bg-gray-900"
                onClick={() => viewQAImage(q.media_url, q.question_text || 'Video', 'video')}>
                {!q.media_url?.startsWith('media_ref:') && <video src={q.media_url} className="h-full w-full object-cover" muted preload="metadata" />}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="w-6 h-6 rounded-full bg-white/80 flex items-center justify-center">
                    <div className="w-0 h-0 border-t-[4px] border-t-transparent border-l-[7px] border-l-gray-800 border-b-[4px] border-b-transparent ml-0.5" />
                  </div>
                </div>
                {q.media_uploaded_by_crm && <div className="absolute top-0.5 left-0.5 bg-blue-600 text-white text-[7px] px-1 rounded">CRM</div>}
              </div>
            ) : (
              <div className="relative flex-shrink-0">
                <img src={q.media_url.startsWith('media_ref:') ? '' : q.media_url} alt=""
                  className="h-14 w-14 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => viewQAImage(q.media_url, q.question_text || 'Photo')}
                  onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56"><rect fill="%23f3f4f6" width="56" height="56"/><text x="28" y="32" text-anchor="middle" fill="%239ca3af" font-size="9">View</text></svg>'; }}
                />
                {q.media_uploaded_by_crm && <div className="absolute top-0.5 left-0.5 bg-blue-600 text-white text-[7px] px-1 rounded">CRM</div>}
              </div>
            )
          )}

          {/* Text answer + sub-answers */}
          <div className="flex-1 min-w-0">
            {isValidTextAnswer && (
              <span className="text-xs font-semibold text-blue-700 block">{formattedAnswer}</span>
            )}
            {/* Sub-questions with answers */}
            {(q.sub_question_1_text || formattedSub1 || q.sub_question_2_text || formattedSub2) && (
              <div className="mt-1.5 space-y-1">
                {q.sub_question_1_text && (
                  <div className="flex items-start gap-1.5" data-testid="sub-question-1-display">
                    <span className="text-[10px] text-gray-500 shrink-0">{q.sub_question_1_text}:</span>
                    <span className={`text-[10px] font-semibold ${formattedSub1 ? 'text-blue-700' : 'text-gray-400 italic'}`}>
                      {formattedSub1 || 'Not answered'}
                    </span>
                  </div>
                )}
                {q.sub_question_2_text && (
                  <div className="flex items-start gap-1.5" data-testid="sub-question-2-display">
                    <span className="text-[10px] text-gray-500 shrink-0">{q.sub_question_2_text}:</span>
                    <span className={`text-[10px] font-semibold ${formattedSub2 ? 'text-orange-700' : 'text-gray-400 italic'}`}>
                      {formattedSub2 || 'Not answered'}
                    </span>
                  </div>
                )}
                {/* Badges for sub-answers without question text */}
                {!q.sub_question_1_text && formattedSub1 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200" data-testid="sub-answer-1-badge">
                    {formattedSub1}
                  </span>
                )}
                {!q.sub_question_2_text && formattedSub2 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-700 border border-orange-200" data-testid="sub-answer-2-badge">
                    {formattedSub2}
                  </span>
                )}
              </div>
            )}
            {/* Fallback: show sub-answer badges when no sub-question text */}
            {!q.sub_question_1_text && !q.sub_answer_1 && q.sub_answer_2 && (
              <div className="flex flex-wrap gap-1 mt-1">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-50 text-orange-700 border border-orange-200" data-testid="sub-answer-2-badge">
                  {typeof q.sub_answer_2 === 'object' ? (q.sub_answer_2.selection || JSON.stringify(q.sub_answer_2)) : q.sub_answer_2}
                </span>
              </div>
            )}
          </div>
        </div>
        {q.answered_at && <p className="text-[9px] text-gray-400">{new Date(q.answered_at).toLocaleString()}</p>}
      </div>
    );
  };

  return (
    <>
      <input ref={fileInputRef} type="file" className="hidden" onChange={onFileSelected} data-testid="qa-media-file-input" />
      <TabsContent value="inspection" className="space-y-0 mt-0" data-testid="qa-details-tab">
        {!inspection?.id ? <TabLoader message="Loading inspection details..." /> : (<>
        {/* Summary Bar */}
        {(() => {
          const cats = liveProgressData?.categories || [];
          const totalAnswered = cats.reduce((s, c) => s + (c.answered_questions || 0), 0);
          const totalQuestions = cats.reduce((s, c) => s + (c.total_questions || 0), 0);
          const pct = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;
          return (
            <div className="bg-white rounded-lg border px-4 py-2.5 mb-3" data-testid="qa-summary-bar">
              <div className="flex items-center gap-3">
                <Activity className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-semibold text-gray-900">Q&A Progress</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${pct === 100 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                  {totalAnswered}/{totalQuestions} ({pct}%)
                </span>
              </div>
            </div>
          );
        })()}

        <div className="flex flex-col" style={{ height: 'calc(100vh - 280px)', minHeight: 400 }}>
          {/* Top: Horizontal Category Scroller */}
          <div className="flex-shrink-0 rounded-lg border bg-white mb-3 overflow-hidden">
            <div className="relative flex items-center" data-testid="qa-category-scroller">
              {/* Left arrow */}
              {showLeftArrow && (
                <button
                  onClick={() => scrollCategories(-1)}
                  className="flex-shrink-0 z-10 px-2 py-2 bg-white border-r hover:bg-gray-50"
                  data-testid="qa-scroll-left"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-600" />
                </button>
              )}
              
              <div
                ref={scrollRef}
                onScroll={checkScrollArrows}
                className="flex items-start gap-2 px-3 py-2 overflow-x-auto hide-scrollbar flex-1 min-w-0"
              >
              <button
                className={`flex-shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${!selectedCategoryId ? 'bg-blue-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                onClick={() => setSelectedCategoryId(null)}
                data-testid="qa-category-all"
              >All Categories</button>

              {(liveProgressData?.categories || []).map((category, idx) => {
                const isComplete = (category.answered_questions || 0) === (category.total_questions || 0) && (category.total_questions || 0) > 0;
                const isSelected = selectedCategoryId === category.category_id;
                const isGeneralInfo = (category.category_name || '').toLowerCase().includes('general information');
                
                // Calculate rating for this category
                const categoryKey = category.category_name?.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
                const rawSaved = editData.category_ratings?.[categoryKey];
                const savedRating = typeof rawSaved === 'object' && rawSaved !== null ? (rawSaved.rating ?? undefined) : rawSaved;
                const aiRating = isInspCompleted ? getAICategoryRating(category.category_name, categoryKey) : null;
                const questions = category.questions || [];
                const answered = questions.filter(q => q.is_answered);
                const goodCount = answered.filter(q => {
                  const a = (typeof q.answer === 'string' ? q.answer : '').toLowerCase();
                  return !a.includes('poor') && !a.includes('bad') && !a.includes('damaged') && !a.includes('broken') && !a.includes('not working') && !a.includes('faulty') && !a.includes('average') && !a.includes('fair') && !a.includes('needs attention') && !a.includes('worn') && !a.includes('minor');
                }).length;
                const autoRating = answered.length > 0 ? Math.round((goodCount / answered.length) * 10) : 0;
                const currentRating = savedRating ?? aiRating ?? (answered.length > 0 ? autoRating : 0);
                const ratingColor = currentRating >= 7 ? 'bg-green-500' : currentRating >= 4 ? 'bg-amber-500' : 'bg-red-500';
                const ratingTextColor = currentRating >= 7 ? 'text-green-700' : currentRating >= 4 ? 'text-amber-700' : 'text-red-700';

                return (
                  <div key={category.category_id || idx} className="flex-shrink-0 flex flex-col items-center gap-1">
                    <button
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 ${isSelected ? 'bg-blue-600 text-white shadow-sm' : isComplete ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      onClick={() => setSelectedCategoryId(category.category_id)}
                      data-testid={`qa-category-${idx}`}
                    >
                      <span className="truncate max-w-[160px]">{category.category_name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isSelected ? 'bg-white/20 text-white' : isComplete ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-500'}`}>
                        {category.answered_questions || 0}/{category.total_questions || 0}
                      </span>
                    </button>
                    {/* Mini rating slider below each category button */}
                    {!isGeneralInfo && (
                      <div className="flex items-center gap-1 w-full px-1" data-testid={`qa-category-rating-${idx}`}>
                        <span className={`text-[9px] font-bold ${ratingTextColor} w-4 text-center`}>{currentRating}</span>
                        <div className="flex-1 min-w-[60px]">
                          <Slider
                            value={[currentRating]}
                            min={0} max={10} step={1}
                            className="h-3 [&_[role=slider]]:h-2.5 [&_[role=slider]]:w-2.5 [&_[role=slider]]:border-0 [&_[data-orientation=horizontal]]:h-1"
                            onValueChange={([val]) => {
                              if (canEdit) {
                                const updated = { ...(editData.category_ratings || {}), [categoryKey]: val };
                                updateField('category_ratings', updated);
                              }
                            }}
                            disabled={!canEdit}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
              
              {/* Right arrow */}
              {showRightArrow && (
                <button
                  onClick={() => scrollCategories(1)}
                  className="flex-shrink-0 z-10 px-2 py-2 bg-white border-l hover:bg-gray-50"
                  data-testid="qa-scroll-right"
                >
                  <ChevronRight className="h-4 w-4 text-gray-600" />
                </button>
              )}
            </div>

            {/* Rating bar for selected category */}
            {selectedCategoryId && (() => {
              const category = (liveProgressData?.categories || []).find(c => c.category_id === selectedCategoryId);
              if (!category) return null;
              const isGeneralInfo = (category.category_name || '').toLowerCase().includes('general information');
              if (isGeneralInfo) return null;
              const categoryKey = category.category_name?.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
              const rawSaved = editData.category_ratings?.[categoryKey];
              const savedRating = typeof rawSaved === 'object' && rawSaved !== null ? (rawSaved.rating ?? undefined) : rawSaved;
              const aiRating = isInspCompleted ? getAICategoryRating(category.category_name, categoryKey) : null;
              const questions = category.questions || [];
              const answered = questions.filter(q => q.is_answered);
              const goodCount = answered.filter(q => {
                const a = (typeof q.answer === 'string' ? q.answer : '').toLowerCase();
                return !a.includes('poor') && !a.includes('bad') && !a.includes('damaged') && !a.includes('broken') && !a.includes('not working') && !a.includes('faulty') && !a.includes('average') && !a.includes('fair') && !a.includes('needs attention') && !a.includes('worn') && !a.includes('minor');
              }).length;
              const autoRating = answered.length > 0 ? Math.round((goodCount / answered.length) * 10) : 0;
              const currentRating = savedRating ?? aiRating ?? (answered.length > 0 ? autoRating : 0);
              const ratingColor = currentRating >= 7 ? 'text-green-700' : currentRating >= 4 ? 'text-amber-700' : 'text-red-700';

              return (
                <div className="flex items-center gap-4 px-4 py-2 border-t bg-gray-50/50" data-testid="qa-selected-category-rating">
                  <span className="text-xs font-semibold text-gray-700 flex-shrink-0">{category.category_name}</span>
                  <span className="text-[10px] text-gray-500 flex-shrink-0">Rating</span>
                  <span className={`text-sm font-bold ${ratingColor} flex-shrink-0`}>{currentRating}/10</span>
                  {canEdit && (
                    <div className="w-48 flex-shrink-0">
                      <Slider
                        value={[currentRating]}
                        min={0} max={10} step={1}
                        onValueChange={([val]) => {
                          const updated = { ...(editData.category_ratings || {}), [categoryKey]: val };
                          updateField('category_ratings', updated);
                        }}
                        className="w-full"
                        data-testid={`qa-rating-slider-selected`}
                      />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Questions Grid - Full width */}
          <div className="flex-1 overflow-hidden rounded-lg border bg-white flex flex-col">
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-3">
              {categoriesToDisplay?.map((category, catIdx) => {
                const categoryKey = category.category_name?.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
                const isGeneralInfo = (category.category_name || '').toLowerCase().includes('general information');
                const rawSaved = editData.category_ratings?.[categoryKey];
                const savedRating = typeof rawSaved === 'object' && rawSaved !== null ? (rawSaved.rating ?? undefined) : rawSaved;
                const aiRating = isInspCompleted ? getAICategoryRating(category.category_name, categoryKey) : null;
                const questions = category.questions || [];
                const answered = questions.filter(q => q.is_answered);
                const goodCount = answered.filter(q => {
                  const a = (typeof q.answer === 'string' ? q.answer : '').toLowerCase();
                  return !a.includes('poor') && !a.includes('bad') && !a.includes('damaged') && !a.includes('broken') && !a.includes('not working') && !a.includes('faulty') && !a.includes('average') && !a.includes('fair') && !a.includes('needs attention') && !a.includes('worn') && !a.includes('minor');
                }).length;
                const autoRating = answered.length > 0 ? Math.round((goodCount / answered.length) * 10) : 0;
                const currentRating = savedRating ?? aiRating ?? (answered.length > 0 ? autoRating : 0);
                const ratingColor = currentRating >= 7 ? 'text-green-700' : currentRating >= 4 ? 'text-amber-700' : 'text-red-700';

                return (
                <div key={category.category_id || catIdx} className={catIdx > 0 ? 'mt-4' : ''}>
                  {!selectedCategoryId && (
                    <div className="flex items-center gap-3 mb-2 pb-1.5 border-b border-gray-100">
                      <span className="text-xs font-bold text-gray-700">{category.category_name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${(category.answered_questions || 0) === (category.total_questions || 0) && (category.total_questions || 0) > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {category.answered_questions || 0}/{category.total_questions || 0}
                      </span>
                      {!isGeneralInfo && (
                        <div className="flex items-center gap-2 ml-auto">
                          <span className="text-[10px] text-gray-500">Rating</span>
                          <span className={`text-xs font-bold ${ratingColor}`}>{currentRating}/10</span>
                          {canEdit && (
                            <div className="w-32">
                              <Slider
                                value={[currentRating]}
                                min={0} max={10} step={1}
                                onValueChange={([val]) => {
                                  const updated = { ...(editData.category_ratings || {}), [categoryKey]: val };
                                  updateField('category_ratings', updated);
                                }}
                                className="w-full"
                                data-testid={`qa-rating-slider-${catIdx}`}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ columns: 2, columnGap: '0.5rem' }}>
                    {category.questions?.map((q, qIdx) => {
                      const isEditing = editingAnswer?.questionId === q.question_id;
                      const hasOptions = q.options && q.options.length > 0;
                      const currentAnswer = typeof q.answer === 'object' ? (q.answer?.selection || JSON.stringify(q.answer)) : q.answer;
                      const expectedMedia = getExpectedMediaType(q);
                      const isUploading = uploadingMedia === q.question_id;

                      return (
                        <div
                          key={q.question_id}
                          data-testid={`qa-question-${catIdx}-${qIdx}`}
                          className={`group rounded-lg border p-2.5 transition-all relative min-w-0 overflow-hidden ${q.is_answered ? 'bg-green-50/60 border-green-200 hover:border-green-300' : 'bg-gray-50/60 border-gray-200 hover:border-gray-300'}`}
                          style={{ breakInside: 'avoid', marginBottom: '0.5rem' }}
                        >
                          {/* Question Header */}
                          <div className="flex items-start gap-2">
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded mt-0.5 flex-shrink-0 ${q.is_answered ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-500'}`}>
                              {qIdx + 1}
                            </span>
                            <p className="text-xs font-medium text-gray-800 leading-snug flex-1 line-clamp-2">{q.question_text}</p>
                            {/* Edit/Add button — always visible for unanswered, hover for answered */}
                            {canEdit && !isEditing && (
                              <button
                                onClick={() => setEditingAnswer({
                                  questionId: q.question_id,
                                  categoryId: category.category_id,
                                  pendingAnswer: currentAnswer || '',
                                  pendingSub1: typeof q.sub_answer_1 === 'object' ? (q.sub_answer_1?.selection || '') : (q.sub_answer_1 || ''),
                                  pendingSub2: typeof q.sub_answer_2 === 'object' ? (q.sub_answer_2?.selection || '') : (q.sub_answer_2 || ''),
                                })}
                                className={`p-1 rounded flex-shrink-0 transition-all ${
                                  q.is_answered
                                    ? 'opacity-0 group-hover:opacity-100 hover:bg-blue-100'
                                    : 'opacity-100 bg-blue-50 hover:bg-blue-100 border border-blue-200'
                                }`}
                                title={q.is_answered ? 'Edit answer' : 'Answer this question'}
                                data-testid={`qa-edit-btn-${catIdx}-${qIdx}`}
                              >
                                {q.is_answered ? <Pencil className="h-3 w-3 text-blue-600" /> : <Plus className="h-3.5 w-3.5 text-blue-600" />}
                              </button>
                            )}
                          </div>

                          {/* Answer Area */}
                          <div className="mt-1.5 ml-6">
                            {isEditing ? (
                              /* ─── EDIT MODE ─── */
                              <div className="space-y-2 bg-white rounded border p-2.5" data-testid={`qa-edit-panel-${catIdx}-${qIdx}`}>
                                {/* MCQ dropdown */}
                                {hasOptions && (
                                  <div>
                                    <label className="text-[10px] font-medium text-gray-500 mb-1 block">Answer</label>
                                    <Select
                                      value={editingAnswer?.pendingAnswer || ''}
                                      onValueChange={(val) => setEditingAnswer(prev => ({ ...prev, pendingAnswer: val }))}
                                      disabled={savingAnswer || isUploading}
                                    >
                                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select answer" /></SelectTrigger>
                                      <SelectContent>{q.options.map((opt, i) => (<SelectItem key={i} value={opt}>{opt}</SelectItem>))}</SelectContent>
                                    </Select>
                                  </div>
                                )}

                                {/* Sub-question 1 */}
                                {q.sub_question_1_text && (
                                  <div>
                                    <label className="text-[10px] font-semibold text-blue-600 mb-1 block">{q.sub_question_1_text}</label>
                                    {q.sub_options_1?.length > 0 ? (
                                      <Select
                                        value={editingAnswer?.pendingSub1 || ''}
                                        onValueChange={(val) => setEditingAnswer(prev => ({ ...prev, pendingSub1: val }))}
                                        disabled={savingAnswer || isUploading}
                                      >
                                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                                        <SelectContent>{q.sub_options_1.map((opt, i) => (<SelectItem key={i} value={opt}>{opt}</SelectItem>))}</SelectContent>
                                      </Select>
                                    ) : (
                                      <input
                                        type="text"
                                        className="w-full h-8 text-xs border rounded px-2"
                                        value={editingAnswer?.pendingSub1 || ''}
                                        onChange={(e) => setEditingAnswer(prev => ({ ...prev, pendingSub1: e.target.value }))}
                                        placeholder="Enter answer..."
                                        disabled={savingAnswer || isUploading}
                                      />
                                    )}
                                  </div>
                                )}

                                {/* Sub-question 2 */}
                                {q.sub_question_2_text && (
                                  <div>
                                    <label className="text-[10px] font-semibold text-orange-600 mb-1 block">{q.sub_question_2_text}</label>
                                    {q.sub_options_2?.length > 0 ? (
                                      <Select
                                        value={editingAnswer?.pendingSub2 || ''}
                                        onValueChange={(val) => setEditingAnswer(prev => ({ ...prev, pendingSub2: val }))}
                                        disabled={savingAnswer || isUploading}
                                      >
                                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                                        <SelectContent>{q.sub_options_2.map((opt, i) => (<SelectItem key={i} value={opt}>{opt}</SelectItem>))}</SelectContent>
                                      </Select>
                                    ) : (
                                      <input
                                        type="text"
                                        className="w-full h-8 text-xs border rounded px-2"
                                        value={editingAnswer?.pendingSub2 || ''}
                                        onChange={(e) => setEditingAnswer(prev => ({ ...prev, pendingSub2: e.target.value }))}
                                        placeholder="Enter answer..."
                                        disabled={savingAnswer || isUploading}
                                      />
                                    )}
                                  </div>
                                )}

                                {/* Current media preview */}
                                {q.media_url && (
                                  <div className="flex items-center gap-2">
                                    <label className="text-[10px] font-medium text-gray-500">Current:</label>
                                    {getExpectedMediaType(q) === 'video' ? (
                                      <div className="h-10 w-16 rounded border overflow-hidden bg-gray-900 flex-shrink-0">
                                        <video src={q.media_url} className="h-full w-full object-cover" muted preload="metadata" />
                                      </div>
                                    ) : (
                                      <img src={q.media_url} alt="" className="h-10 w-10 object-cover rounded border flex-shrink-0"
                                        onError={(e) => { e.target.style.display = 'none'; }} />
                                    )}
                                  </div>
                                )}

                                {/* Media upload */}
                                {expectedMedia && (
                                  <div>
                                    <label className="text-[10px] font-medium text-gray-500 mb-1 block">
                                      {q.media_url ? 'Replace' : 'Upload'} {expectedMedia === 'video' ? 'Video' : 'Photo'}
                                    </label>
                                    <Button
                                      variant="outline" size="sm"
                                      className="h-7 text-[10px] px-2 gap-1"
                                      disabled={isUploading || savingAnswer}
                                      onClick={() => handleMediaUpload(q.question_id, expectedMedia === 'video')}
                                      data-testid={`qa-upload-media-${catIdx}-${qIdx}`}
                                    >
                                      {expectedMedia === 'video' ? <Film className="h-3 w-3" /> : <Camera className="h-3 w-3" />}
                                      {expectedMedia === 'video' ? 'Choose Video' : 'Choose Photo'}
                                    </Button>
                                    {isUploading && (
                                      <div className="mt-1.5">
                                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                          <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                                        </div>
                                        <span className="text-[9px] text-blue-600 mt-0.5 block">Uploading... {uploadProgress}%</span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Actions */}
                                <div className="flex items-center gap-1.5 pt-1 border-t">
                                  <Button
                                    variant="default" size="sm"
                                    className="h-6 text-[10px] px-2"
                                    disabled={savingAnswer || isUploading}
                                    onClick={() => {
                                      const val = editingAnswer?.pendingAnswer;
                                      if (val || editingAnswer?.pendingSub1 || editingAnswer?.pendingSub2) {
                                        updateAnswer(q.question_id, val, q.options, editingAnswer?.pendingSub1, editingAnswer?.pendingSub2);
                                      }
                                    }}
                                    data-testid={`qa-save-answer-${catIdx}-${qIdx}`}
                                  >
                                    {savingAnswer ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => setEditingAnswer(null)} disabled={savingAnswer || isUploading} className="h-6 text-[10px] px-2">
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : q.is_answered ? (
                              /* ─── VIEW MODE (answered) ─── */
                              renderAnswerDisplay(q, currentAnswer)
                            ) : (
                              /* ─── UNANSWERED ─── */
                              <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                                <Clock className="h-2.5 w-2.5" />
                                <span>Pending</span>
                                {expectedMedia && <span className="text-purple-500 font-medium">({expectedMedia})</span>}
                                {hasOptions && <span className="text-blue-500">({q.options.length} opts)</span>}
                                {q.sub_question_1_text && <span className="text-blue-500 font-medium">+sub-q</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )})}

              {(!categoriesToDisplay || categoriesToDisplay.length === 0) && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <ClipboardList className="h-10 w-10 mb-2 opacity-40" />
                  <p className="text-sm">No Q&A data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
        </>)}
      </TabsContent>
    </>
  );
};

export default QATab;
